import { computed, ref, onMounted } from 'vue';
import { getRecordType, getInfoBucket, getAgencyDisplayName, toDateOrNull, daysBetweenLocalDays } from '@/utils/formatters.js';
import { reportLinkFrom, reviewedCaseCount, hasRequestsMade, arsAddedSinceLastRequest, notReceivedCount, isMarkedDone, isMarkedStale, revCasesVerdict, revCasesRunCount, revCasesLastRun, parseSyncedRatings, agencyShorthand, CMD_TYPE } from '@/composables/useFollowUpState.js';
import { buildAgencyLookup } from '@/utils/tableShared.js';
import { REPORT_SERVER } from '@/composables/useReportOpener.js';

// ─── Report status ───────────────────────────────────────────────────────
// "Does this informational batch/list still need review, and is anything
// sitting Pending at the agency?" — extracted from LibraryView.vue (the
// Agency Reports tab's per-batch status pills) so the Agencies table can
// show the same Needs Review / Info Pending counts without re-deriving the
// New/Unreviewed/Reviewed/Done rules and risking the two surfaces drifting.

// ─── Shared review-status fetch ───────────────────────────────────────────
// /api/rev-cases-review-status walks the whole report library (~0.5s for ~550
// reports) and used to run on every mount of every view using this composable
// (Library, AI Reports, Agencies — each fully remounts on navigation). State
// now lives at module level so all consumers share one copy, concurrent
// mounts share one in-flight request, and a successful result is reused for
// REVIEW_STATUS_TTL_MS. The TTL is short on purpose: a report's picks are saved
// in another browser, and coming back to the dashboard shortly after should
// still see them. A FAILED fetch is never cached, so the next mount retries,
// and force=true (the banner's Retry) always bypasses the cache.
const REVIEW_STATUS_TTL_MS = 30_000;
const sharedReviewStatusMap = ref({});
const sharedReportAges = ref({});
const sharedNewDeliveries = ref({});
const sharedStatusServerError = ref(null);
let reviewStatusFetchedAt = 0;
let reviewStatusInFlight = null;

function loadSharedReviewStatus(force = false) {
  if (reviewStatusInFlight) return reviewStatusInFlight;
  if (!force && reviewStatusFetchedAt && Date.now() - reviewStatusFetchedAt < REVIEW_STATUS_TTL_MS) {
    return Promise.resolve();
  }
  reviewStatusInFlight = (async () => {
    try {
      const res = await fetch(`${REPORT_SERVER}/api/rev-cases-review-status`);
      if (!res.ok) throw new Error(`server returned ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      sharedReviewStatusMap.value = data.statuses || {};
      sharedReportAges.value = data.reportAges || {};
      sharedNewDeliveries.value = data.newDeliveries || {};
      sharedStatusServerError.value = null;
      reviewStatusFetchedAt = Date.now();
    } catch (e) {
      sharedReviewStatusMap.value = {};
      sharedReportAges.value = {};
      sharedNewDeliveries.value = {};
      sharedStatusServerError.value = e?.message || 'unreachable';
      reviewStatusFetchedAt = 0;
    } finally {
      reviewStatusInFlight = null;
    }
  })();
  return reviewStatusInFlight;
}

// Refetch when the tab/window wakes up (added 2026-10-10). The fetch above only ran on
// MOUNT, so a Library tab left open never saw reports regenerated in the meantime:
// after 19 reports were regenerated their records pointed at new report URLs the open
// page had no status entry for, and Needs Review showed 2 instead of 19 until a hard
// reload. loadSharedReviewStatus() already honours REVIEW_STATUS_TTL_MS, so waking the
// tab repeatedly costs at most one request per 30 s, and a failed fetch is never cached.
let wakeRefreshBound = false;
function bindWakeRefresh() {
  if (wakeRefreshBound || typeof window === 'undefined' || typeof document === 'undefined') return;
  wakeRefreshBound = true;
  const onWake = () => { if (document.visibilityState !== 'hidden') loadSharedReviewStatus(); };
  window.addEventListener('focus', onWake);
  document.addEventListener('visibilitychange', onWake);
}

// Library's inclusion rule, shared so callers outside the composable (the
// record and agency modals' "Open Library" buttons, via Home.vue) can ask "would this
// agency show up in Library?" without instantiating the whole composable and
// its report-server fetch. See the long comment above reportBearingRecords
// for why each branch exists.
const IN_HAND_STAGES = ['Received', 'Bullpen', 'BullpenX'];
function includeInLibrary(r) {
  return !!(
    r.reportUrl || r.reportUnlinkable
    || isMarkedDone(r.otherInfo) || isMarkedStale(r.otherInfo)
    || (r._bucket !== 'ars' && IN_HAND_STAGES.includes((r.stage || '').trim()))
    // ARs batches skip the blanket in-hand inclusion above (see comment
    // above IN_HAND_STAGES) to avoid resurfacing old batches that were
    // reviewed by hand before "Save Rating Only" synced case ratings back
    // — those typically sit at rating 0/blank, not -1. rating === -1 is
    // the narrower, precise signal: it's the "Unknown" flag
    // RecordEditDialog auto-sets on entering Received/Bullpen only when
    // nothing had rated the record yet (see RecordEditDialog.vue), so it
    // only catches batches genuinely untouched since receipt.
    || (r._bucket === 'ars' && IN_HAND_STAGES.includes((r.stage || '').trim()) && r.rating === -1)
  );
}
// Same test against a raw Firestore record (reportBearingRecords applies it
// to an already-mapped one).
export function isLibraryRecord(r) {
  if (getRecordType(r) !== 'informational') return false;
  return includeInLibrary({ ...r, ...reportLinkFrom(r.otherInfo), _bucket: getInfoBucket(r) });
}

// What each status pill means, for its hover tooltip (2026-10-17). The
// per-record `description` from statusFor() goes underneath when there is
// one. Unreviewed and Refreshed are green shades (2026-10-17: "proceed" --
// ready to pick from); New stays amber and Part Rated orange (2026-10-25: amber = run a tool, green = ready for you, orange = finish what you started, grey = nothing to do). Reviewed is a pale
// slate (2026-10-18, was green): it is off the review list, so it shouldn't
// share a color family with the statuses that still need a look.
const STATUS_HELP = {
  'New': 'Needs review. Nothing has been done yet: for an ARs batch, rev-ar hasn\'t made a report; for an annual list, rev-cases has never run on it.',
  'Unreviewed': 'Needs review. ARs batch: report ready, no ratings synced. Annual list: screened once, no batch pulled yet.',
  'Part Rated': 'Needs review. Some cases in this ARs batch are rated, the rest aren\'t, and no footage request has been made.',
  'New Installment': 'Needs rev-ar. The agency sent more ARs for this batch since rev-ar last ran, and some cases were still Not Received. Re-run rev-ar; the batch then moves to New Ratings Needed.',
  'New Ratings Needed': 'Needs review. rev-ar has read ARs that arrived after this batch\'s last footage request, and they aren\'t all rated yet. Rate them in the report.',
  'Refreshed': 'Needs review. Re-screened, with recommended cases not yet picked.',
  'Reviewed': 'Reviewed. For an ARs batch, its ratings are synced. For an annual list, its picks were saved in the report, or it predates review tracking.',
  'Mined': 'Off the review list. An ARs batch was pulled from this list, and no newer rev-cases run recommends anything still unpicked.',
  'Mined (untracked)': 'Off the review list. An ARs batch already cites this list, but rev-cases never screened it.',
  'Waiting': 'Not in hand yet: submitted or paid, awaiting the agency.',
  'Pay?': 'The agency quoted a fee. Decide whether to pay.',
  'On Hold': 'The case is still open, so the agency won\'t release it yet.',
  'No Footage': 'The agency doesn\'t have this or won\'t provide it.',
  'Ghosted': 'No response from the agency. Counted as a failure.',
  'Low Value': 'Rated 1–1.5★: the list has no usable case data to screen.',
  'Done': 'Closed: marked DONE in Other Info.',
  'Stale': 'Closed: marked STALE in Other Info (deprioritized, not fully worked).',
};
export function statusHelp(label) {
  return STATUS_HELP[label] || '';
}
// What to do next, per pill, for the Library's empty right pane (2026-10-25), shown
// beside each pill. Keep in step with STATUS_HELP above and the labels/colors
// statusFor() returns. `review` marks the six that put a row on Needs Review
// (needsReviewStatusOptions in LibraryView.vue), so that tab lists only those.
const STATUS_GUIDE = [
  { label: 'New', color: 'amber-darken-2', review: true, text: 'Have AI review it: run rev-ar (ARs) or rev-cases (lists).' },
  { label: 'New Installment', color: 'amber-darken-2', review: true, text: 'Run rev-ar again to read the newly arrived ARs.' },
  { label: 'Unreviewed', color: 'light-green-darken-3', review: true, text: 'Open the report; rate the cases (ARs) or pick a batch (lists).' },
  { label: 'Refreshed', color: 'green-darken-1', review: true, text: 'Open the report and pick from the recommended cases.' },
  { label: 'Part Rated', color: 'orange-darken-2', review: true, text: 'Finish rating the remaining cases.' },
  { label: 'New Ratings Needed', color: 'orange-darken-2', review: true, text: 'Rate the ARs that arrived after your last request.' },
  { label: 'Pay?', color: 'orange-darken-3', text: 'Decide whether to pay the quote.' },
  { label: 'Reviewed', color: 'blue-grey-lighten-3', text: 'Nothing to do. Ratings synced or picks saved.' },
  { label: 'Mined', color: 'blue-grey-darken-1', text: 'Nothing to do. A batch was already pulled from this list.' },
  { label: 'Waiting', color: 'blue-grey-darken-1', text: 'Nothing to do. Submitted or paid; agency has not delivered.' },
  { label: 'On Hold', color: 'blue-grey-darken-1', text: 'Nothing to do. Case still open; agency will not release yet.' },
  { label: 'No Footage', color: 'grey-darken-1', text: 'Nothing to do. Agency does not have it or will not provide it.' },
  { label: 'Ghosted', color: 'grey-darken-1', text: 'Nothing to do. No response; counted as a failure.' },
  { label: 'Low Value', color: 'grey-darken-1', text: 'Nothing to do. Rated 1–1.5★; no usable case data.' },
  { label: 'Done', color: 'grey-darken-1', text: 'Nothing to do. Closed: DONE in Other Info.' },
  { label: 'Stale', color: 'grey-darken-1', text: 'Nothing to do. Closed: deprioritized, not fully worked.' },
];
export function statusGuide(reviewOnly = false) {
  return reviewOnly ? STATUS_GUIDE.filter((g) => g.review) : STATUS_GUIDE;
}
// A status pill's text as one or two lines (2026-10-24, user: "Can the Pill
// for New Ratings Needed be put on two rows?"). A label of three or more words
// breaks after the second ("New Ratings" / "Needed"), so it doesn't widen the
// Status column for every row. Pair with the status-chip-two-line class
// (App.vue) when this returns two lines.
export function statusLabelLines(label) {
  const words = String(label || '').split(' ');
  return words.length >= 3 ? [words.slice(0, 2).join(' '), words.slice(2).join(' ')] : [label];
}

export function useReportStatus(recordsRef, agenciesRef) {
  // Has a HUMAN confirmed a rev-cases report's batch (💾 Save picks), as
  // opposed to the AI's own screening verdict landing in otherInfo
  // automatically at generation time (see CLAUDE.md's rev-cases Step 5 —
  // it runs in the same command invocation as generation, not a separate
  // human action). Keyed by reportUrl, matching what /api/rev-cases-picks
  // writes into each report's own META block and
  // /api/rev-cases-review-status reads back out. Fetched once on mount;
  // an unmatched URL (server not running, or a report this scan couldn't
  // read) is treated as "unknown" rather than "unreviewed" by statusFor()
  // below — same fail-safe-to-quiet default as every other localhost-
  // dependent Library feature, so a stopped report server can't make a
  // batch of already-fine reports suddenly look like they need review.
  const reviewStatusMap = sharedReviewStatusMap;
  // Report file mtime (ISO date), keyed by reportUrl — added 2026-09-14 for
  // the Library "Report Age" column ("how long since this was last actually
  // regenerated," separate from Source Age's "how long since the underlying
  // material arrived" — see reportBearingRecords below). Same response body
  // as reviewStatusMap above, a separate top-level field so it never
  // interacts with reviewStatusMap's picksSavedAt-gated grandfathering (see
  // that map's own comment, and serve_reports.py's send_rev_cases_review_status).
  // Covers both rev-cases-*.html and rev-ar's *Review*.html, so it's
  // populated for either bucket, unlike reviewStatusMap (rev-cases only).
  const reportAges = sharedReportAges;
  // rev-ar reports whose batch folder has ARs delivered after them, keyed by
  // reportUrl (2026-10-24, Bonney Lake 2025 ARs 1). Same response body;
  // see serve_reports.py's send_rev_cases_review_status and
  // scripts/library_new_deliveries.py for what counts as delivered.
  const newDeliveries = sharedNewDeliveries;
  // Set when the status fetch fails (server down, non-200, or the server's own
  // {error} payload). statusFor() deliberately falls back to "grandfathered
  // Reviewed" for a report with no map entry, so a failed fetch quietly shrinks
  // every Needs Review count — this lets the UI say so instead of looking fine.
  const statusServerError = sharedStatusServerError;
  // Cached load on mount; pass true to force a fresh fetch (Retry button).
  const loadReviewStatus = (force = false) => loadSharedReviewStatus(force === true);
  onMounted(() => { loadReviewStatus(); bindWakeRefresh(); });

  // "[Year] [SourceType]" fragment matching what create-ars-record writes to
  // howFound (e.g. "2024 Arrests", "2025 UOF Report" — see
  // .claude/commands/create-ars-record.md Step 4A). Extracted from a source
  // list's own name by taking the year token onward, which discards any
  // agency prefix the same way howFound already omits it — "Lancaster 2025
  // UOF Report" and howFound's "2025 UOF Report" normalize to the same
  // string. Only meaningful for logs/uof/complaints records (the ARs side is
  // matched by howFound directly, not by re-deriving a fragment from its own
  // name).
  //
  // A partial-year month prefix ("9M", "9-12M") is year-FIRST in howFound ("2026 9M
  // Arrests"), but older source records were named with it before the year ("Harvard PD
  // 9M 2026 Arrests"), which made the fragment "2026 arrests" and never matched the
  // batch's howFound. Move a leading prefix behind the year so both spellings agree.
  function sourceFragmentOf(name) {
    const s = String(name || '').trim();
    const pre = /(?:^|\s)(\d{1,2}(?:-\d{1,2})?M)\s+(20\d{2})\s*(.*)$/i.exec(s);
    if (pre) return `${pre[2]} ${pre[1]} ${pre[3]}`.trim().toLowerCase();
    const m = /(20\d{2})\s*(.*)$/.exec(s);
    return m ? `${m[1]} ${m[2]}`.trim().toLowerCase() : null;
  }

  // Every ARs batch's howFound, grouped by the agency it was requested
  // under — built once from the raw record set (not reportBearingRecords,
  // which doesn't exist yet at this point). Keyed loosely (lowercased,
  // trimmed) since howFound is free-typed.
  //
  // Matched by CONTAINS, not exact equality (widened 2026-09-14, after an
  // audit of the 16 lists an earlier version of this logic incorrectly hid
  // from Needs Review found 4 — Alpharetta PD, Roswell (GA) PD, East Point
  // PD, MARTA PD — that an exact match silently failed on): these agencies'
  // 2024 arrests all came from one shared Fulton County Sheriff export (the
  // multi-agency split rev-cases handles), so create-ars-record correctly
  // wrote howFound as "Fulton County Sheriff 2024 Arrests" (its own
  // documented "[Parent Agency] [Year] [SourceType]" form) or, in one case,
  // "2024 Arrests (Fulton)" — neither equals the municipality's own plain
  // "2024 arrests" fragment, but both CONTAIN it. Checked against all 16
  // audited lists' real howFound values before shipping: every genuine match
  // (11 exact, 4 prefixed/suffixed) matches under contains, and no
  // unrelated howFound for the same agency accidentally matches, since a
  // different year or report type breaks the contiguous substring (e.g.
  // "2024 UOF and Arrests" does not contain "2024 arrests" — "uof and" sits
  // between the year and the word).
  //
  // Each howFound maps to the request date of its latest ARs batch (null when
  // no batch carries a date), so hasUnpulledRecommendations() can tell
  // whether a rev-cases run came after the last batch pulled from the list.
  const minedHowFoundByAgency = computed(() => {
    const map = new Map();
    (recordsRef.value || []).forEach((r) => {
      if (getInfoBucket(r) !== 'ars') return;
      const hf = String(r.howFound || '').trim().toLowerCase();
      if (!hf) return;
      const agency = getAgencyDisplayName(r).trim().toLowerCase();
      if (!map.has(agency)) map.set(agency, new Map());
      const byHf = map.get(agency);
      const d = toDateOrNull(r.displayDate);
      const prev = byHf.get(hf) || null;
      byHf.set(hf, d && (!prev || d > prev) ? d : prev);
    });
    return map;
  });
  function minedBatchDates(item) {
    const frag = sourceFragmentOf(item.name);
    if (!frag) return [];
    const byHf = minedHowFoundByAgency.value.get((item._agencyDisplay || '').trim().toLowerCase());
    if (!byHf) return [];
    return [...byHf].filter(([hf]) => hf.includes(frag)).map(([, d]) => d);
  }
  function alreadyMined(item) {
    return minedBatchDates(item).length > 0;
  }
  // A rev-cases run on a later day than the last ARs batch pulled from the
  // list, whose suggested batch still holds cases: those recommendations
  // were never selected, so the list is not Mined, it needs a look
  // (2026-10-17, Vermilion 2025 Arrests: "If it is listed as Mined I will
  // not review it. If a rev-cases has recommended cases that were not
  // selected it should be status of Refreshed"). A batch pulled the same day
  // as the run counts as pulled from that run.
  function hasUnpulledRecommendations(item) {
    const run = revCasesLastRun(item.otherInfo);
    if (!run.date || !(run.batchCount > 0)) return false;
    const lastBatch = minedBatchDates(item).reduce((a, d) => (d && (!a || d > a) ? d : a), null);
    if (!lastBatch) return false;
    return daysBetweenLocalDays(lastBatch, toDateOrNull(run.date)) > 0;
  }

  // One display name per agency, keyed the same way every downstream lookup is
  // (trimmed + lowercased — see infoPendingByAgency, useAgencyCapacity,
  // buildAgencyLookup). Records whose agency differs only by casing, e.g.
  // "North Chicago (Lake)" vs "North Chicago (lake)" (a real pair in the
  // 2026-09-20 export), used to become two Library rows because grouping and
  // selection compare the exact string. Canonical name = the AgencyInfo
  // record's own spelling when there is one, else the most common variant.
  //
  // Deliberately NOT merging suffix variants ("Melbourne" / "Melbourne PD"):
  // every downstream lookup above keys on the lowercased name, so a merged
  // display name would silently miss the other variant's slots/pending counts.
  // No such pair exists in current data; doing it properly means changing
  // those keys too.
  const canonicalAgencyNames = computed(() => {
    const variants = new Map();
    (recordsRef.value || []).forEach((r) => {
      const raw = getAgencyDisplayName(r);
      const key = raw.toLowerCase();
      if (!key) return;
      if (!variants.has(key)) variants.set(key, new Map());
      const counts = variants.get(key);
      counts.set(raw, (counts.get(raw) || 0) + 1);
    });
    const lookup = buildAgencyLookup(agenciesRef.value || []);
    const out = new Map();
    for (const [key, counts] of variants) {
      const doc = lookup.get(key);
      const official = doc ? String(doc.agencyName || doc.Agency || doc.name || '').trim() : '';
      const best = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0];
      out.set(key, official || best);
    }
    return out;
  });
  function canonicalAgency(raw) {
    return canonicalAgencyNames.value.get((raw || '').toLowerCase()) || raw;
  }

  // Shows any informational record carrying a Report: link, regardless of
  // which pipeline wrote it. rev-AR writes its own link automatically on
  // every run; rev-cases only gets one when the operator does Step 5
  // write-back for that particular list — so a rev-cases report appears
  // here exactly when it's been linked, same as everything else.
  //
  // WIDENED 2026-08-23 for logs/uof/complaints only (Arrests/UOF/Complaints
  // annual lists), after two earlier attempts at widening this gate for ALL
  // buckets — including 'ars' — shipped to production and had to be
  // reverted: neither checked whether a "never linked" batch was actually
  // already mined, so a batch fully handled elsewhere (an existing ARs batch
  // pulled from it under the old per-agency-legacy-script era, or via manual
  // work that predates the canonical rev-cases Report: link convention) got
  // flagged "Not Screened" as if the cases inside it were still open to
  // request — a real risk of resurfacing already-requested cases as
  // pickable. Fixed by cross-checking every logs/uof/complaints candidate
  // against minedHowFoundByAgency (above) via howFound before it can ever read as
  // needing review — see statusFor(). The 'ars' bucket is deliberately left
  // untouched (still requires a real link): an ARs batch's own "already
  // handled" signal is unsynced per-case ratings (rev-ar-ratings-recovery.md
  // — 💾 Save Rating Only went essentially unused before 2026-08-18), a
  // different check this pass doesn't attempt. (The inclusion test itself,
  // and IN_HAND_STAGES, now live at module level as includeInLibrary.)
  const reportBearingRecords = computed(() => {
    const now = new Date();
    return (recordsRef.value || [])
      .filter((r) => getRecordType(r) === 'informational')
      .map((r) => {
        const link = reportLinkFrom(r.otherInfo);
        const yearMatch = /(20\d{2})/.exec(r.name || '');
        const batchMatch = /ARs\s*-?\s*(\d+)/i.exec(r.name || '');
        const received = toDateOrNull(r.receivedDate);
        const requested = toDateOrNull(r.displayDate);
        const basis = received || requested;
        // "Report Age" — days since this record's linked report was last
        // actually regenerated (file mtime, via reportAges above). A
        // different question from Source Age (_ageDays/_ageBasis below,
        // "how long since the underlying material arrived"): a batch
        // received two years ago but re-run last week has an old Source Age
        // and a fresh Report Age, and the two shouldn't be collapsed into
        // one number — see the comment on reportAges above. null when the
        // link isn't a real reportUrl (reportUnlinkable-only, or no report
        // at all) or the report server has no mtime for it (server down, or
        // a report this scan couldn't read).
        const reportGenerated = link.reportUrl ? toDateOrNull(reportAges.value[link.reportUrl]) : null;
        return {
          ...r,
          ...link,
          _agencyDisplay: canonicalAgency(getAgencyDisplayName(r)),
          _bucket: getInfoBucket(r),
          _year: yearMatch ? parseInt(yearMatch[1], 10) : null,
          _batch: batchMatch ? parseInt(batchMatch[1], 10) : 0,
          _ageDays: basis ? daysBetweenLocalDays(basis, now) : null,
          _ageBasis: received ? 'received' : (requested ? 'requested' : null),
          _stale: basis ? daysBetweenLocalDays(basis, now) >= 548 : false,
          _reportAgeDays: reportGenerated ? daysBetweenLocalDays(reportGenerated, now) : null,
          // Same mtime as a date, for the agency panel's Report Date column — a
          // date (not an age) so a list's re-run can be compared directly
          // against the ARs batch pulled from it (Monroe (IL) 2026 8M Arrests,
          // re-run 10/11 after ARs 1, read "Refreshed" with nothing to show why).
          _reportDate: reportGenerated,
        };
      })
      .filter(includeInLibrary);
  });

  // Stages where an informational record (ARs, or a logs/uof/complaints
  // annual list) has no document in hand yet (or never will), so treating it
  // as "New"/"Unreviewed" would be misleading — see CLAUDE.md Pipeline /
  // Stage Definitions. Only IN_HAND_STAGES (Received/Bullpen/BullpenX) actually
  // means a document exists to screen or rate. Action is deliberately left
  // out: per CLAUDE.md it can mean either "I need to act" or "waiting on the
  // agency", and either way a document may already be in hand, so it still
  // falls through to the screening/ratings checks below rather than getting a
  // fixed label. Used by both branches of statusFor() below — originally
  // ARs-only (hence the old name), widened 2026-09-07 to the logs/uof/
  // complaints branch too, which had no equivalent check and mislabeled a
  // still-Pending list "New" (see the "New" case's own comment there).
  const NO_DOCUMENT_STAGES = {
    'Pending': { label: 'Waiting', color: 'blue-grey-darken-1', description: 'Submitted, awaiting the agency' },
    'Paid': { label: 'Waiting', color: 'blue-grey-darken-1', description: 'Paid — awaiting delivery' },
    'Quote': { label: 'Pay?', color: 'orange-darken-3', description: 'Agency quoted a fee — awaiting your pay/pass decision' },
    'Open': { label: 'On Hold', color: 'blue-grey-darken-1', description: 'Case still open — agency will not release yet' },
    'No Footage': { label: 'No Footage', color: 'grey-darken-1', description: 'Agency does not have or will not provide this' },
    'Ghosted': { label: 'Ghosted', color: 'grey-darken-1', description: 'No response — treated as a failure' },
  };

  // Informational records genuinely still in flight at the agency — no
  // document in hand, so they never make it into reportBearingRecords above
  // (that set exists to drive a REPORTS table, and there's no report to open
  // yet). Deliberately narrower than NO_DOCUMENT_STAGES: 'No Footage' and
  // 'Ghosted' are resolved outcomes (a failure, not something "awaiting"),
  // so they're left out here rather than clutter an in-flight list with dead
  // ends — infoPendingByAgency/the Library agency-list pill already cover
  // the narrower Stage=Pending count elsewhere. Reuses NO_DOCUMENT_STAGES'
  // labels rather than inventing new ones, since the wording (Waiting/Pay?/On
  // Hold) is already generic across ars vs logs/uof/complaints.
  const AWAITING_AGENCY_STAGES = ['Pending', 'Paid', 'Quote', 'Open'];
  const pendingInfoRecords = computed(() => {
    const now = new Date();
    const alreadyShown = new Set(reportBearingRecords.value.map((r) => r.id));
    return (recordsRef.value || [])
      .filter((r) => getRecordType(r) === 'informational')
      .filter((r) => AWAITING_AGENCY_STAGES.includes((r.stage || '').trim()))
      .filter((r) => !isMarkedDone(r.otherInfo) && !isMarkedStale(r.otherInfo))
      .filter((r) => !alreadyShown.has(r.id))
      .map((r) => {
        const yearMatch = /(20\d{2})/.exec(r.name || '');
        const batchMatch = /ARs\s*-?\s*(\d+)/i.exec(r.name || '');
        const requested = toDateOrNull(r.displayDate);
        return {
          ...r,
          _agencyDisplay: canonicalAgency(getAgencyDisplayName(r)),
          _bucket: getInfoBucket(r),
          _year: yearMatch ? parseInt(yearMatch[1], 10) : null,
          _batch: batchMatch ? parseInt(batchMatch[1], 10) : 0,
          _ageDays: requested ? daysBetweenLocalDays(requested, now) : null,
          _stageStatus: NO_DOCUMENT_STAGES[(r.stage || '').trim()] || null,
        };
      });
  });

  // ARs batches carry per-case ratings synced from the report's "Save Rating
  // Only" button into the Update field — Reviewed/Unreviewed is a meaningful
  // status there. Arrests/UOF/Complaints lists never get per-case ratings;
  // their real progress signal is rev-cases' own screening verdict in
  // otherInfo.
  // A later installment of an ARs batch that rev-ar hasn't read yet: files in
  // the batch folder newer than its report, while the report still counts
  // cases Not Received (2026-10-24, Bonney Lake 2025 ARs 1). The existing New
  // ARs check below only fires after rev-ar is re-run, which nothing prompted.
  // Stale stays closed (deliberately deprioritized, the same rule rev-ar's
  // reopen-on-new-ARs follows); the delivery entry clears itself once the
  // re-run writes a newer report.
  function pendingInstallment(item) {
    if (item._bucket !== 'ars' || !item.reportUrl || isMarkedStale(item.otherInfo)) return null;
    if (notReceivedCount(item.otherInfo) <= 0) return null;
    return newDeliveries.value[item.reportUrl] || null;
  }

  function statusFor(item) {
    // Checked before Done: a batch closed on its first installment is exactly
    // the one that otherwise never comes back. An explicit DONE outranking
    // new material is still the rule for everything else (see below).
    const installment = pendingInstallment(item);
    if (installment) {
      return {
        // Its own status, not New Ratings Needed (user, 2026-10-24: "easily tell which
        // requires rev-ar and which requires human review"). Amber like New:
        // both mean a tool has to run before there's anything to rate.
        label: 'New Installment', color: 'amber-darken-2',
        // Short: it shows under the batch name (user: "I don't need file
        // names"); the pill's tooltip already says to re-run rev-ar.
        description: `${installment.newFiles} new file${installment.newFiles === 1 ? '' : 's'} since rev-ar last ran`,
        needsReview: true,
      };
    }
    // A hand-set Content Rating of 1-1.5★ means the list itself has no usable
    // case data (no case #/suspect name to chase, or only a single thin
    // charge) — checked before anything else, including Done/Stale/a fresh
    // regeneration below, since it's a deliberate human data-quality call
    // that outranks any automatic verdict. -1 (Unknown) and 0/blank (unrated)
    // are deliberately excluded — only an explicit low rating counts, never
    // the absence of one. Only meaningful for logs/uof/complaints; the ars
    // bucket has its own rules below.
    if (item._bucket !== 'ars' && typeof item.rating === 'number' && item.rating > 0 && item.rating <= 1.5) {
      return {
        label: 'Low Value', color: 'grey-darken-1',
        description: 'Rated 1–1.5★ — no usable case data to screen',
        needsReview: false,
      };
    }

    // A hand-set DONE/STALE marker in Other Info closes a batch outright —
    // see CLAUDE.md's DONE/STALE conventions. Reordered 2026-09-06 to run
    // BEFORE the fresh-regeneration check below: that check used to override
    // an explicit DONE/STALE whenever the linked report had been regenerated
    // same-day with cases left unconfirmed, on the theory that a real
    // regeneration always means there's new material worth a look. The user
    // decided the opposite tradeoff is what they want: once a batch is
    // deliberately closed, it stays closed until reopened by hand — even if
    // a later /rev-cases re-run turns up more qualifying cases (a busier
    // source list, a loosened exclusion). That means a DONE/STALE batch that
    // keeps growing will NOT resurface here on its own; re-check it manually
    // if that's ever a concern for a given agency/list.
    if (isMarkedDone(item.otherInfo)) {
      return { label: 'Done', color: 'grey-darken-1', description: null, needsReview: false };
    }
    if (isMarkedStale(item.otherInfo)) {
      return { label: 'Stale', color: 'grey-darken-1', description: 'Deliberately deprioritized — not fully worked', needsReview: false };
    }

    // A real /rev-cases regeneration ALWAYS rewrites Firestore's verdict line
    // (CLAUDE.md/rev-cases.md: "refresh means regenerate... STEP 5's Firestore
    // write-back runs too, per the note above — that's expected, not a side
    // effect to avoid"). So a report with cases still to mine means review is
    // needed — UNLESS Done/Stale already closed the batch above, in which case
    // we never reach here at all. Checked only when the linked report actually
    // carries trackable review state (a picksSavedAt key present in
    // reviewStatusMap); an untracked link (predating 2026-09-04, or the server
    // down) has no freshness signal to act on, so it falls through to the
    // grandfathered Reviewed check below exactly as before.
    //
    // Deliberately NOT gated on reviewEntry.reviewed (fixed 2026-09-14,
    // Champaign Sheriff 2025 Arrests): picksSavedAt just means Save Picks
    // fired at least once — auto-save fires on every single pick toggle, so
    // one click on one case sets it, the same as picking and confirming the
    // whole list. Gating on it meant a report with 51 of 61 cases still
    // unmined read as "Reviewed" and vanished from Needs Review the instant
    // a single pick (or, that day, a UI bug) touched it once. The user's
    // actual rule: until a batch is Done/Stale, it stays in Needs Review —
    // Save Picks having fired before doesn't retire that, and neither does
    // verdict.remaining reaching 0.
    //
    // Also NOT gated on verdict.remaining > 0 (fixed 2026-09-14, same day,
    // Tumwater PD 2025 Arrests): a `remaining === 0` verdict means every
    // qualifying case the algorithm found got proposed into the suggested
    // batch — it does NOT mean a human confirmed anything, or that the batch
    // was actually submitted. A prior version of this check treated
    // remaining === 0 as its own terminal "Exhausted" state (needsReview:
    // false) the instant a rev-cases run happened to fit its whole find into
    // one batch — which turned out to be common (a first-ever run on a
    // smaller/newer list often does), and meant a batch nobody had ever
    // looked at could vanish from Needs Review before the operator got a
    // chance to act on it via this list. Done/Stale retires a batch
    // explicitly; so does actually mining it (alreadyMined(item), below).
    //
    // alreadyMined(item) ALONE is now enough for a Mined verdict (changed
    // 2026-09-15, Rio Rancho 2025 Arrests) — see the alreadyMined check
    // inside the block below for the reasoning and its precedence relative
    // to Refreshed (an existing batch outranks a re-run since 2026-10-17).
    if (item._bucket !== 'ars') {
      const reviewEntry = item.reportUrl ? reviewStatusMap.value[item.reportUrl] : null;
      if (reviewEntry) {
        const verdict = revCasesVerdict(item.otherInfo);
        if (verdict.screened) {
          const caseWord = verdict.remaining > 0
            ? `${verdict.remaining} case${verdict.remaining === 1 ? '' : 's'} awaiting confirmation`
            : 'batch proposed — awaiting confirmation';
          // alreadyMined(item) ALONE is enough here (changed 2026-09-15, Rio
          // Rancho 2025 Arrests): this used to also require verdict.remaining
          // === 0 (every qualifying case proposed, none left over), so a
          // list with 65 of 75 qualifying cases never proposed still read
          // Unreviewed despite two real ARs batches already pulled from it.
          // Per the user: the actual workflow is request a batch, come back
          // for more later, repeat until the list is marked Done or Stale —
          // a partially-mined list on its FIRST-EVER run is not an
          // un-reviewed one, no matter how much qualifying material is still
          // sitting there. alreadyMined(item) is real, human-confirmed
          // evidence (a genuine /create-ars-record write, never an AI-only
          // signal), unlike the two conditions this block already refuses to
          // gate on above (see the comments before this reviewEntry block),
          // so dropping remaining===0 here isn't the same failure mode as
          // either of those.
          //
          // Checked BEFORE Refreshed (changed 2026-10-17, reversing the
          // 2026-09-15 order). Re-running a list after a batch was pulled
          // from it used to read Refreshed, on the theory that a re-run meant
          // freshly-arrived material. In practice it never did: all 40
          // Refreshed lists in the 2026-10-17 export were re-screens of the
          // same material (rule changes, maintenance re-runs), and the 6
          // re-run after their latest batch all had a source file older than
          // that batch (Vermilion 2025 Arrests: PDF 3/11, last batch 7/24,
          // re-runs 9/6 and 10/10). User: "Why is it Refreshed if it was
          // already used. That is misleading." New material for a list
          // arrives as its own record (a top-off, CLAUDE.md "Partial-Year
          // Annual Lists"), which has no batch yet and so still reads
          // Refreshed/Unreviewed below.
          //
          // Except when the latest run is newer than the latest batch and
          // still recommends cases (changed later the same day, Vermilion
          // 2025 Arrests again, see hasUnpulledRecommendations): Mined hides
          // a list from review, so it must only mean nothing recommended is
          // left unpulled. Such a list reads Refreshed, since a batch was
          // already pulled from it before.
          if (alreadyMined(item) && hasUnpulledRecommendations(item)) {
            const description = `Re-screened after the last ARs batch — ${caseWord}`;
            return { label: 'Refreshed', color: 'green-darken-1', description, needsReview: true };
          }
          if (alreadyMined(item)) {
            return {
              label: 'Mined', color: 'blue-grey-darken-1',
              description: 'ARs batch already requested from this list',
              needsReview: false,
            };
          }
          // "Refreshed" means THIS list has been through rev-cases before —
          // gated on run count, not on whether a prior pass was ever
          // confirmed. A busy agency's list may never get Save-Picks-clicked
          // and may never reach Done/Stale either (it just keeps growing
          // every re-run), but it's still been genuinely refreshed, and a
          // first-ever screening (run count 1) is not the same thing as a
          // list that's been re-run and left hanging again.
          if (revCasesRunCount(item.otherInfo) > 1) {
            const description = reviewEntry.previouslyReviewedAt
              ? `Reviewed ${reviewEntry.previouslyReviewedAt} — refreshed since, ${caseWord}`
              : `Re-screened before — refreshed since, ${caseWord}`;
            return { label: 'Refreshed', color: 'green-darken-1', description, needsReview: true };
          }
          return { label: 'Unreviewed', color: 'light-green-darken-3', description: caseWord, needsReview: true };
        }
      }
    }

    if (item._bucket === 'ars') {
      // Checked before the ratings read: a record can pick up a report link
      // (e.g. reportUnlinkable from a stale file:// note) before it's
      // actually been received.
      const noDocument = NO_DOCUMENT_STAGES[(item.stage || '').trim()];
      if (noDocument) {
        return { label: noDocument.label, color: noDocument.color, description: noDocument.description, needsReview: false };
      }
      // "New" vs "Unrated" — both have no synced case ratings, but they need
      // different next actions. No report link at all (and not flagged
      // reportUnlinkable, which means a report DOES exist, just under an old
      // unmigrated file:// link) means rev-ar has never even been run on
      // this batch — the action is "generate the report." A report link
      // present but no synced ratings means the report exists and may
      // already have been read — the action is "open it and sync ratings."
      // Distinguishing them matters most for the batches this reportBearingRecords
      // rating===-1 inclusion (above) newly surfaces, which are otherwise
      // indistinguishable from an old batch whose sync just never happened.
      if (!item.reportUrl && !item.reportUnlinkable) {
        return { label: 'New', color: 'amber-darken-2', description: 'Received — no report generated yet', needsReview: true };
      }
      const synced = parseSyncedRatings(item.update);
      if (!synced.size) return { label: 'Unreviewed', color: 'light-green-darken-3', description: null, needsReview: true };
      const counts = { Yes: 0, Maybe: 0, No: 0 };
      for (const rating of synced.values()) {
        if (counts[rating] !== undefined) counts[rating] += 1;
      }
      const parts = ['Yes', 'Maybe', 'No']
        .filter((k) => counts[k])
        .map((k) => `${counts[k]} ${k}`);
      // A partly rated batch (some cases rated, the rest untouched) used to
      // read as Reviewed on the first synced rating. Rev-AR's "Reviewed: N" is
      // the case total. Validated against the 2026-09-20 export: most partly
      // rated batches (21 of 24 not Done/Stale) already have "N request(s)
      // made" — rate the ones you want, act, skip the rest — so those stay
      // Reviewed (with the x-of-y shown). Only a partial with NO request made
      // is stalled work and returns to Needs Review.
      const total = reviewedCaseCount(item.otherInfo);
      const partial = total && synced.size < total;
      // ...unless ARs arrived after that request (2026-10-22): those cases
      // were never looked at, so the batch is back to needing review. The
      // generator also drops a DONE marker when a re-run adds ARs, so a
      // closed batch reaches here too.
      const added = arsAddedSinceLastRequest(item.otherInfo);
      if (partial && added > 0) {
        return {
          // Was "New ARs" (2026-10-22); renamed 2026-10-24 so the label says
          // what to do, like New Installment does.
          label: 'New Ratings Needed', color: 'orange-darken-2',
          description: `${added} AR${added === 1 ? '' : 's'} added since the last request — ${synced.size} of ${total} rated${parts.length ? ` (${parts.join(', ')})` : ''}`,
          needsReview: true,
        };
      }
      if (partial && !hasRequestsMade(item.otherInfo)) {
        return {
          label: 'Part Rated', color: 'orange-darken-2',
          description: `${synced.size} of ${total} rated, none requested yet${parts.length ? ` — ${parts.join(', ')}` : ''}`,
          needsReview: true,
        };
      }
      const detail = parts.join(', ');
      return {
        label: 'Reviewed', color: 'blue-grey-lighten-3',
        description: partial ? `${synced.size} of ${total} rated${detail ? ` — ${detail}` : ''}` : (detail || null),
        needsReview: false,
      };
    }

    // logs / uof / complaints — annual lists screened by rev-cases, not
    // rated case-by-case. needsReview here means "rev-cases has never
    // looked at this at all". Low Value, Done/Stale, and the
    // fresh-unconfirmed-regeneration override are all handled above.
    //
    // Checked before the rev-cases verdict, same as the ars branch above: a
    // Pending/Paid/Quote/Open/No Footage/Ghosted list has no document in
    // hand yet, so "rev-cases has never screened it" is trivially true for a
    // reason that has nothing to do with needing review — the list hasn't
    // even arrived. Added 2026-09-07: this check was missing here (it only
    // existed for the ars branch), so a still-Pending list like "Marta 2025
    // Arrests" fell straight through to the "New" case below and rendered
    // identically to a Received-but-unscreened list — collapsing "waiting on
    // the agency" and "in hand, needs a look" into the same label.
    const noDocument = NO_DOCUMENT_STAGES[(item.stage || '').trim()];
    if (noDocument) {
      return { label: noDocument.label, color: noDocument.color, description: noDocument.description, needsReview: false };
    }
    const verdict = revCasesVerdict(item.otherInfo);
    if (!verdict.screened) {
      // Before calling this untouched, check whether an ARs batch was
      // already pulled from it by some other route (see alreadyMined above)
      // — that's "handled, just untracked here", not "needs review".
      if (alreadyMined(item)) {
        return {
          label: 'Mined (untracked)', color: 'blue-grey-darken-1',
          description: 'An ARs batch already references this list, but rev-cases has never screened it directly',
          needsReview: false,
        };
      }
      // Same meaning as the ARs bucket's "New" (nothing has happened yet) —
      // shared vocabulary across both pipelines, see useReportStatus.js's
      // header comment on reviewStatusMap for why the buckets otherwise
      // diverge from here.
      return { label: 'New', color: 'amber-darken-2', description: 'rev-cases has never run on this list', needsReview: true };
    }
    // By this point the report is either untracked (no reviewStatusMap entry
    // — grandfathered, predates 2026-09-04 tracking) or Save-Picks-confirmed
    // and re-screened with nothing new: a tracked, unconfirmed report — with
    // cases remaining OR fully proposed into its batch (remaining === 0) —
    // would already have returned Refreshed/Unreviewed above, unless
    // Done/Stale closed the batch first. verdict.remaining reaching 0 is not
    // its own terminal state (see the comment above the reviewEntry block):
    // only an explicit Done/Stale marker retires a batch now.
    return {
      label: 'Reviewed', color: 'blue-grey-lighten-3',
      description: verdict.remaining > 0
        ? `${verdict.remaining} case${verdict.remaining === 1 ? '' : 's'} remaining to mine`
        : null,
      needsReview: false,
    };
  }

  // ─── Command to run ─────────────────────────────────────────────────────
  // The exact command to paste into Claude Code for a row that still needs
  // review — same agencyShorthand()/CMD_TYPE syntax commandFor() in
  // useFollowUpState.js uses for Action Center, reused directly rather than
  // re-derived so the two surfaces can't drift on how a command is built.
  // Deliberately NOT routed through useFollowUpState's own commandFor(),
  // though: that function reasons about Action Center's pool-exhaustion /
  // blocked-by-an-outstanding-batch states, which answer a different
  // question ("is there new material worth finding") than Needs Review asks
  // ("has a human confirmed this batch yet") — see statusFor()'s Refreshed/
  // Unreviewed comments above. Needs Review already knows everything this
  // needs from its own bucket/year/batch, and "refresh" is just re-running
  // the same command per the project's own "refresh means regenerate"
  // convention, so no extra state machine is needed to decide WHICH command
  // — only whether one applies at all.
  //
  // Deliberately NOT gated on statusFor(item).needsReview: that flag means
  // "still needs a human look," but a command is useful any time re-running
  // the tool could do something — including an already-Reviewed/Mined batch,
  // where a fresh /rev-cases run can still turn up newly-arrived source rows
  // (or rev-ar's report/rating sync can still be refreshed). Reviewed is not
  // retired. What genuinely rules out a command: the batch is explicitly
  // closed (Done/Stale — re-running it is exactly what those markers say not
  // to do, unless a Done batch has a pending installment), there's no document in hand yet to run a tool against
  // (NO_DOCUMENT_STAGES — nothing to screen or rate), or the list itself was
  // judged not worth screening at all (Low Value, 1-1.5★, logs/uof/
  // complaints only — same rating check statusFor() makes above).
  function runCommandFor(item) {
    if (isMarkedStale(item.otherInfo)) return null;
    if (isMarkedDone(item.otherInfo) && !pendingInstallment(item)) return null;
    if (NO_DOCUMENT_STAGES[(item.stage || '').trim()]) return null;
    if (item._bucket !== 'ars' && typeof item.rating === 'number' && item.rating > 0 && item.rating <= 1.5) return null;
    const agency = agencyShorthand(item);
    if (!agency) return null;
    const year = item._year || '';
    if (item._bucket === 'ars') {
      // Unnumbered "[Agency] YYYY ARs" is batch 1 by convention (matches
      // nextArsBatchNumber's fallback in useFollowUpState.js).
      const batch = item._batch || 1;
      return `rev-ar ${agency} ${year} ${batch}`.replace(/\s+/g, ' ').trim();
    }
    const type = CMD_TYPE[item._bucket];
    if (!type) return null;
    return `/rev-cases ${agency} ${year} ${type}`.replace(/\s+/g, ' ').trim();
  }

  const agencyLookup = computed(() => buildAgencyLookup(agenciesRef.value || []));
  function agencyDetailsFor(name) {
    return agencyLookup.value.get((name || '').trim().toLowerCase()) || {};
  }

  // Avoid-rated agencies excluded, same convention as Action Center/
  // Acquisition Queue — hard-excluded from every recommended-action
  // surface, not just demoted.
  const needsReviewRecords = computed(() => {
    return reportBearingRecords.value
      .filter((r) => statusFor(r).needsReview)
      .filter((r) => agencyDetailsFor(r._agencyDisplay).hotStatus !== 4);
  });

  // Array form (sorted by count desc) — backs the top-nav pill's per-agency
  // breakdown tooltip.
  const needsReviewByAgency = computed(() => {
    const map = new Map();
    needsReviewRecords.value.forEach((r) => {
      const name = r._agencyDisplay;
      if (!name) return;
      map.set(name, (map.get(name) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([agency, count]) => ({ agency, count }))
      .sort((a, b) => b.count - a.count || a.agency.localeCompare(b.agency));
  });

  // Map form, keyed lowercase-trimmed — for O(1) per-row lookup (Agencies table).
  const needsReviewCountByAgency = computed(() => {
    const map = new Map();
    needsReviewRecords.value.forEach((r) => {
      const key = (r._agencyDisplay || '').trim().toLowerCase();
      if (!key) return;
      if (!map.has(key)) map.set(key, { agency: r._agencyDisplay, count: 0, names: [] });
      const entry = map.get(key);
      entry.count += 1;
      entry.names.push(r.name);
    });
    return map;
  });

  // Informational (ARs / Primary Source) requests the AGENCY is still
  // actively engaged on — Pending (submitted, awaiting the list/report),
  // Quote (they've engaged and quoted a fee) or Paid (fee paid, awaiting
  // delivery). This gates the Library "Has Capacity" toggle and the
  // 2-at-a-time guideline, so it's meant to answer "is this agency already
  // handling something for me," not "do I have a document in hand yet" —
  // Open is deliberately excluded even though it also has no document yet:
  // per CLAUDE.md, Open is a case-still-open legal hold on the COURT, not
  // the agency's records office, and can sit that way indefinitely without
  // reflecting anything about the agency's workload — counting it would make
  // an agency read as permanently "full" for a reason unrelated to capacity.
  // (Contrast pendingInfoRecords below, which asks the narrower "no document
  // in hand" question and does include Open for that reason.)
  // Sourced from recordsRef directly rather than reportBearingRecords: a
  // record still awaiting the agency has no report link yet, so it would
  // never appear in the report-bearing set.
  //
  // A DONE/STALE marker in Other Info excludes a record here too, added
  // 2026-09-09: STALE means "deliberately deprioritized... not worth
  // revisiting" (CLAUDE.md), which includes not chasing the agency for it
  // any further — so it shouldn't occupy a capacity slot or inflate the
  // Info Pending badge, the same way it already doesn't count toward
  // needsReviewRecords above.
  const INFO_PENDING_STAGES = ['Pending', 'Quote', 'Paid'];
  const infoPendingByAgency = computed(() => {
    const map = new Map();
    (recordsRef.value || []).forEach((r) => {
      if (getRecordType(r) !== 'informational') return;
      if (!INFO_PENDING_STAGES.includes((r.stage || '').trim())) return;
      if (isMarkedDone(r.otherInfo) || isMarkedStale(r.otherInfo)) return;
      const display = canonicalAgency(getAgencyDisplayName(r));
      const key = display.trim().toLowerCase();
      if (!key) return;
      if (!map.has(key)) map.set(key, { agency: display, count: 0, names: [] });
      const entry = map.get(key);
      entry.count += 1;
      entry.names.push(r.name);
    });
    return map;
  });

  return {
    reportBearingRecords,
    statusFor,
    runCommandFor,
    needsReviewRecords,
    needsReviewByAgency,
    needsReviewCountByAgency,
    infoPendingByAgency,
    pendingInfoRecords,
    statusServerError,
    loadReviewStatus,
    reportAges,
  };
}
